import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { API_URLS } from '../constants/apiUrl';
import { useLoading } from '../context/LoadingContext';
import { patch } from '../api/api';
import { showToast } from '../components/utils/toast';
import { ITask, IUpdateTask } from '../interface/tasks';
import { IAPIError } from '../interface/common';
import { AnalyticsFeatureOperation } from '../interface/analytics';
import { useAnalytics } from './useAnalytics';

const updateTask = async (
  taskId: string,
  taskData: IUpdateTask,
  params: Record<string, string>,
): Promise<ITask> => {
  return patch(`${API_URLS.TASKS_LIST}/${taskId}`, taskData, {
    params,
    skipGlobalErrorHandler: true,
  });
};

export const useUpdateTask = (): UseMutationResult<
  ITask,
  AxiosError<IAPIError>,
  { taskId: string; taskData: IUpdateTask; params: Record<string, string> }
> => {
  const { setIsLoading } = useLoading();
  const { t } = useTranslation();
  const { trackBusinessEvent } = useAnalytics();

  return useMutation({
    mutationFn: ({ taskId, taskData, params }) => updateTask(taskId, taskData, params),
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: () => {
      trackBusinessEvent(AnalyticsFeatureOperation.TASK_UPDATE);
      showToast('success', t('task:successfullyEdited'));
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });
};
